import { z } from 'zod';
import { FindingStatusSchema, SeveritySchema, } from '../domain/finding.js';
import { CloudNodeTypeSchema } from './cloud-graph.js';
import { IdSchema, IsoDateTimeSchema } from './common.js';
import { ReachSchema } from './proof-path.js';
/**
 * Exposure Debt (ADR 0010): one figure for how much proved exposure is open across the
 * organisation, and what would pay it down.
 *
 *   debt = Reach × Threat × Residual gap        Index = Σ debt
 *
 * Every weight is a constant here, shown on the page beside the figure, so a person can recompute
 * any number from what is on screen. No part of it comes from a model.
 */
export const REACH_WEIGHT = {
    SECRETS: 10,
    DATA: 6,
    COMPUTE: 3,
    EDGE_ONLY: 1,
    NOT_IN_ACCOUNT: 1,
};
export const SEVERITY_WEIGHT = {
    CRITICAL: 10,
    HIGH: 7,
    MEDIUM: 4,
    LOW: 2,
    INFORMATIONAL: 0.5,
};
/** Known exploitation raises threat: in CISA KEV, or EPSS at or above the threshold. */
export const KEV_MULTIPLIER = 1.5;
export const EPSS_MULTIPLIER = 1.2;
export const EPSS_THRESHOLD = 0.1;
/** What is left of a finding's exposure after its triage decision. */
export const RESIDUAL_WEIGHT = {
    UNVERIFIED: 1,
    CONFIRMED: 1,
    ACCEPTED_RISK: 0.6,
    FIXED: 0,
    FALSE_POSITIVE: 0,
};
export const ExposureWeaknessSchema = z.enum([
    'VULNERABLE_COMPONENT',
    'SECURITY_HEADERS',
    'CROSS_ORIGIN',
    'INFORMATION_DISCLOSURE',
    'OTHER',
]);
export const ExposureItemSchema = z.object({
    findingId: IdSchema,
    engagementId: IdSchema,
    applicationId: IdSchema,
    title: z.string().max(300),
    severity: SeveritySchema,
    status: FindingStatusSchema,
    weakness: ExposureWeaknessSchema,
    reach: ReachSchema,
    kev: z.boolean(),
    epss: z.number().min(0).max(1).nullable(),
    /** reach × threat × residual, rounded to one decimal. */
    debt: z.number().nonnegative(),
});
export const ExposureMonthSchema = z.object({
    /** `YYYY-MM`. */
    month: z.string().regex(/^\d{4}-\d{2}$/),
    index: z.number().nonnegative(),
    newFindings: z.number().int().nonnegative(),
    closed: z.number().int().nonnegative(),
});
export const ExposureFixSchema = z.object({
    remediationId: IdSchema,
    engagementId: IdSchema,
    title: z.string().max(300),
    /** PROVED — a re-scan proved it; the debt is removed. PENDING — open or merged, not yet proved. */
    state: z.enum(['PROVED', 'PENDING']),
    findings: z.number().int().nonnegative(),
    debt: z.number().nonnegative(),
    pullRequestUrl: z.string().max(2048).optional(),
});
/** A hop between an entry and what it reaches, and the debt that cutting it would remove. */
export const ExposureCutSchema = z.object({
    nodeId: z.string().max(2048),
    type: CloudNodeTypeSchema,
    name: z.string().max(512),
    /** Findings whose reach would fall if this hop were cut. */
    findings: z.number().int().nonnegative(),
    debtReduction: z.number().nonnegative(),
    /** Where the reach goes after it, most sensitive first. */
    reaches: z.array(z.string().max(512)).max(5),
});
/** A live figure for each CTEM stage. */
export const CtemLoopSchema = z.object({
    scope: z.object({ applications: z.number().int(), approvedScopes: z.number().int() }),
    discover: z.object({ publicHosts: z.number().int(), unownedHosts: z.number().int() }),
    prioritize: z.object({ index: z.number(), openFindings: z.number().int() }),
    validate: z.object({ provedPaths: z.number().int(), reachSensitive: z.number().int() }),
    mobilize: z.object({ fixesPending: z.number().int(), fixesProved: z.number().int() }),
});
/** GET /v1/exposure */
export const ExposureSchema = z.object({
    generatedAt: IsoDateTimeSchema,
    index: z.number().nonnegative(),
    /** The index at the end of last month, from the same reconstruction as the trend. */
    previousIndex: z.number().nonnegative().nullable(),
    /** False when the knowledge base was not reachable: threat is then severity only. */
    threatIntel: z.boolean(),
    loop: CtemLoopSchema,
    trend: z.array(ExposureMonthSchema).max(12),
    byWeakness: z.array(z.object({ weakness: ExposureWeaknessSchema, findings: z.number().int(), debt: z.number() })),
    byReach: z.array(z.object({ reach: ReachSchema, findings: z.number().int(), debt: z.number() })),
    byApplication: z.array(z.object({
        applicationId: IdSchema,
        name: z.string().max(120),
        findings: z.number().int(),
        debt: z.number(),
    })),
    fixes: z.array(ExposureFixSchema).max(50),
    cuts: z.array(ExposureCutSchema).max(10),
    /** The open items behind the index, largest debt first. */
    items: z.array(ExposureItemSchema).max(500),
});
//# sourceMappingURL=exposure.js.map